import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  FormLabel,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Radio,
  Button,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { WotcSurveyData } from './types';
import { Box, css } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import { usePostWotcSurvey } from '@features/wotc/hooks/usePostWotcSurvey';
import { toast } from 'react-toastify';
import { staticFileLocation } from '@server/constants';
import dayjs, { Dayjs } from 'dayjs';

type UnemploymentFormModalProps = {
  longTermUnEmployedFormModal: boolean;
  handleClose: () => void;
  wotcSurveyData: WotcSurveyData;
};
const defaultValues = {
  unemployment1: 'Y',
  unemployment2: 'no',
  unemployment1a: null as Dayjs | null,
  unemployment1b: null as Dayjs | null,
};

const UnemploymentFormModal: FC<UnemploymentFormModalProps> = ({
  longTermUnEmployedFormModal,
  handleClose,
  wotcSurveyData,
}) => {
  const [errorText, setErrorText] = useState('');
  const { sendWotcSurvey, isSendingWotcSurvey } = usePostWotcSurvey({
    setErrorText,
  });

  const { control, watch, handleSubmit, setValue, formState: { errors }, } = useForm({
    defaultValues,
  });

  const unemployment1 = watch('unemployment1');
  const unemployment2 = watch('unemployment2');

  const onSubmit = (data: any) => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        toast.success('Data submitted successfully');
        handleClose();
      },
    };

    const transformedData = {
      // ...wotcSurveyData,
      status: 5,
      unemployment1: unemployment1 === 'Y' ? 'Y' : 'N',
      unemployment2: unemployment2 === 'Y' ? 'Y' : 'N',
      unemployment1a: data.unemployment1a
        ? data.unemployment1a.toISOString().split('T')[0]
        : undefined,
      unemployment1b: data.unemployment1b
        ? data.unemployment1b.toISOString().split('T')[0]
        : undefined,
    };

    sendWotcSurvey({ ...transformedData }, options);
  };

  useEffect(() => {
    if (wotcSurveyData) {
      Object.keys(defaultValues).forEach((key) => {
        const typedKey = key as keyof typeof defaultValues &
          keyof WotcSurveyData;
        if (wotcSurveyData[typedKey] !== undefined) {
          if (typedKey === 'unemployment1a' || typedKey === 'unemployment1b') {
            const dateValue = wotcSurveyData[typedKey];
            const date = dateValue ? dayjs(dateValue) : null;
            setValue(typedKey, date);
          } else if (wotcSurveyData[typedKey] !== null) {
            setValue(typedKey, wotcSurveyData[typedKey]);
          }
        }
      });
    }
  }, [wotcSurveyData, setValue]);

  return (
    <Dialog
      open={longTermUnEmployedFormModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', right: 8, top: 8 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap="10px"
        >
          <img
            src={`${staticFileLocation}img/images/icon6.png`}
            alt="Logo"
            css={css`
              max-height: 60px;
              display: block;
            `}
          />
          <Typography variant="h5">Long-Term Unemployment</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          {errorText && (
            <Alert sx={{ mb: 1 }} severity="error">
              {errorText}
            </Alert>
          )}
          <FormControl>
            <Box
              sx={{
                display: 'flex',
                alignItems: {
                  xs: 'start',
                  sm: 'center',
                },
                justifyContent: 'space-between',
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                gap: '4px',
              }}
            >
              <FormLabel component="legend">
                Have you been unemployed for 27 consecutive weeks during the
                last 12 months?
              </FormLabel>
              <Controller
                name="unemployment1"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row sx={{ flexWrap: 'nowrap' }}>
                    <FormControlLabel
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                    <FormControlLabel
                      value="unsure"
                      control={<Radio />}
                      label="Unsure"
                    />
                  </RadioGroup>
                )}
              />
            </Box>
          </FormControl>

          {unemployment1 === 'Y' && (
            <>
              <FormControl>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: {
                      xs: 'start',
                      sm: 'center',
                    },
                    justifyContent: 'space-between',
                    flexDirection: {
                      xs: 'column',
                      sm: 'row',
                    },
                    gap: '10px',
                  }}
                >
                  <FormLabel component="legend">
                    Have you received unemployment compensation in the last 12
                    months?
                  </FormLabel>
                  <Controller
                    name="unemployment2"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field} row sx={{ flexWrap: 'nowrap' }}>
                        <FormControlLabel
                          value="Y"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                        <FormControlLabel
                          value="unsure"
                          control={<Radio />}
                          label="Unsure"
                        />
                      </RadioGroup>
                    )}
                  />
                </Box>
              </FormControl>

              {unemployment2 === 'Y' && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: {
                      xs: 'column',
                      sm: 'row',
                    },
                    gap: 2,
                    mt:2,
                    width: '100%',
                  }}
                >
                  <Box sx={{width:"100%"}}>
                  <Controller
                    name="unemployment1a"
                    control={control}
                    rules={{
                      required: 'First day of unemployment is required',
                    }}
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        sx={{ width: '100%' }}
                        label="First day of unemployment"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                   {errors.unemployment1a && (
                    <span
                      style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                    >
                      {errors.unemployment1a?.message}
                    </span>
                  )}
                  </Box>
                  <Box sx={{width:"100%"}}>
                  <Controller
                    name="unemployment1b"
                    control={control}
                    rules={{ required: 'Last day of unemployment is required' }}
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        sx={{ width: '100%' }}
                        label="Last day of unemployment"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                   {errors.unemployment1b && (
                    <span
                      style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                    >
                      {errors.unemployment1b?.message}
                    </span>
                  )}
                  </Box>
                </Box>
              )}
            </>
          )}

          <Box width="100%" textAlign="center">
            <LoadingButton
              loading={isSendingWotcSurvey}
              sx={{ width: '100%', mt: 3 }}
              type="submit"
              variant="contained"
              color="primary"
            >
              Submit
            </LoadingButton>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UnemploymentFormModal;
